/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { EntityType } from 'embercom/models/data/entity-types';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { ViewId } from 'embercom/services/reporting-views';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import { DRAWER_EDITOR_QUERY_PARAMS } from 'embercom/services/knowledge-hub-drawer-editor-service';
import type { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';

export default class ContentController extends Controller {
  queryParams = [
    'section',
    'openSectionId',
    'openSalesforceSync',
    'salesforceSyncStep',
    { nativeContent: { as: 'native_content', replace: true } },
    ...DRAWER_EDITOR_QUERY_PARAMS,
  ];
  @tracked showSetupModal = false;
  @tracked openSectionId?: string;
  @tracked setupModalEntryPoint?: ViewId = undefined;
  @tracked selectedEntityType: EntityType | null = null;
  @tracked openSalesforceSync?: boolean;
  @tracked salesforceSyncStep?: number;
  @tracked _nativeContent: boolean | undefined = undefined;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare unifiedContentState: UnifiedContentStateService;
  @service navbarCollapsingService: $TSFixMe;

  get nativeContent(): boolean | undefined {
    return this._nativeContent;
  }

  set nativeContent(value: boolean | undefined | string) {
    if (!this.appService.app.canUseUnifiedContentUI) {
      return;
    }

    let normalizedValue: boolean | undefined = typeof value === 'string' ? value === 'true' : value;
    this._nativeContent = normalizedValue;
  }

  @action async closeSetupModal() {
    this.showSetupModal = false;
    this.selectedEntityType = null;
    this.setupModalEntryPoint = undefined;
    await this.knowledgeHubService.removeEmptySources();
    this.knowledgeHubService.onSummaryChange();
    if (this.appService.app.canUseUnifiedContentUI) {
      this.unifiedContentState.refreshContent();
    }
  }

  @tracked showPreview = true;
  @tracked activeContentId?: number;
  @tracked activeContentType?: string;
  @tracked editorMode?: EditorMode;
  @tracked collectionId?: string;
  @tracked contentLocale?: string;
  @tracked folderId?: string;

  @action setOpenSectionId(id: string | undefined) {
    this.openSectionId = id;
  }

  @action
  toggleSidebar() {
    this.navbarCollapsingService.toggleNavbarCollapsed();
    let focusIsOnToggleButton = Boolean(
      document.activeElement?.closest('[data-submenu-collapse-toggle-button]'),
    );
    // we don't want the sidebar button to stay focused after click
    if (focusIsOnToggleButton) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }

  @action
  togglePreview() {
    this.showPreview = !this.showPreview;
  }

  @action
  toggleSalesforceSync() {
    if (this.openSalesforceSync) {
      this.openSalesforceSync = undefined;
      this.salesforceSyncStep = undefined;
    } else {
      this.openSalesforceSync = true;
    }
  }

  @action
  setSalesforceSyncStep(step: number) {
    this.salesforceSyncStep = step;
  }

  @action
  async showModal(syncSourceType: string | undefined, entityType: EntityType) {
    try {
      await this.knowledgeHubService.ensurePermissions(entityType);
    } catch (error) {
      return;
    }

    this.setupModalEntryPoint = syncSourceType as ViewId;
    this.showSetupModal = true;
  }
}
